import { Banner } from '../design-system/Banner';
import { Button } from '../design-system/Button';
import { useNudgeImpression } from '../../hooks/useNudgeImpression';
import { analytics } from '../../lib/analytics';
import { AlertTriangle, Zap } from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import type { FastagProfile } from '../../types';

interface NudgeStickyBannerProps {
  profile: FastagProfile;
  visible: boolean;
  onRecharge: () => void;
  onDismiss: () => void;
  dismissed: boolean;
}

export function NudgeStickyBanner({
  profile,
  visible,
  onRecharge,
  onDismiss,
  dismissed,
}: NudgeStickyBannerProps) {
  useNudgeImpression('nudge_sticky_banner', visible && !dismissed, { balance: profile.balance });

  if (!visible || dismissed) return null;

  const isNegative = profile.balance < 0 || profile.recentDecline;
  const title = isNegative
    ? `FASTag balance is negative ${formatCurrency(profile.balance)}`
    : 'FASTag balance critically low';
  const body = isNegative
    ? `Recharge now to avoid toll plaza blacklisting. Recommended top-up: ${formatCurrency(profile.recommendedAmount)}.`
    : `Your balance may not cover upcoming tolls. Top up ${formatCurrency(profile.recommendedAmount)}.`;

  const handleRecharge = () => {
    analytics.emit('recharge_cta_click', { nudge_id: 'nudge_sticky_banner', balance: profile.balance });
    onRecharge();
  };

  const handleDismiss = () => {
    analytics.emit('nudge_dismiss', { nudge_id: 'nudge_sticky_banner' });
    onDismiss();
  };

  return (
    <div className="mx-4 mb-3" data-testid="nudge-sticky-banner">
      <Banner
        variant={isNegative ? 'error' : 'warning'}
        icon={isNegative ? <AlertTriangle className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
        onDismiss={handleDismiss}
      >
        <div className="space-y-1">
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs leading-relaxed opacity-90">{body}</p>
        </div>
        <Button
          slot="action"
          size="sm"
          fullWidth
          onClick={handleRecharge}
          className="mt-2"
        >
          Recharge {formatCurrency(profile.recommendedAmount)}
        </Button>
      </Banner>
    </div>
  );
}
